//ユーザーCSVアップロードページ
import React, { Fragment, useMemo, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Input,
  Select,
  Spin,
  Alert,
  Space,
  Table,
} from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import Axios from 'axios';
import config from '../../Config/key';
import Papa from 'papaparse';
import {
  decodeCsvBuffer,
  validateCsvHeaders,
  formatHeaderValidationError,
  extractAxiosErrorMessage,
} from '../../../utils/csvUploadHelper';
import {
  buildRosterRows,
  buildRosterErrorCsv,
  buildDepartmentPrefixes,
  completeDepartmentChain,
  joinInputBelong,
  normalizeRecord,
  validateRecordDepartments,
  triggerBlobDownload,
  ymd8,
  buildSampleRosterCsv,
  SAMPLE_CSV_FILE_NAMES,
} from '../../../utils/orgRosterHelper';
import {
  buildCompanyRule,
  useCompanyRule,
  collectCompanyErrors,
  hasCompanyKey,
  mergeRowErrors,
  countErrorKinds,
  formatCompanyErrorDetail,
} from '../../../utils/companyRule';
import { StatusBadge } from '../../common';
// const bcrypt = require('bcrypt');
// const saltRounds = 10;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// CSV に必須の列（email のみ）
const REQUIRED_COLUMNS = ['email'];

// 組織検証エラー行テーブルのカラム（行番号 / 氏名 / 入力された所属 / 理由 / 候補）
const ORG_ERROR_COLUMNS = [
  { title: '行番号', dataIndex: 'row', key: 'row', width: 80 },
  { title: '氏名', dataIndex: 'name', key: 'name', width: 140 },
  { title: '入力された所属', dataIndex: 'belong', key: 'belong' },
  { title: 'エラー理由', dataIndex: 'error', key: 'error' },
  {
    title: '候補',
    dataIndex: 'suggestions',
    key: 'suggestions',
    render: (list) =>
      list && list.length > 0
        ? list.map((s) => s.path).filter((p) => p).join(' / ')
        : '—',
  },
];

function CSVUserUpdatePage(props) {
  const [fileData, setFileData] = useState();
  const [fileJSON, setFileJSON] = useState();
  const [groups, setGroups] = useState([]); //グループ名とグループコード

  let department = [];
  let position = [];
  let empType = [];
  let levelCode = [];
  let goOut = [];
  // const [projectDescriptionValue, setProjectDescriptionValue] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [submitButtonDisable, setSubmitButtonDisable] = useState(true);
  // 処理中フラグ（大量件数で待機が長い間、二重送信防止 + 全画面スピナー）
  const [processing, setProcessing] = useState(false);

  // ─── 組織マスター検証（名簿連携） ───
  // orgVersion: undefined=未判定 / null=組織表未登録（従来方式）/ object=有効バージョンあり
  const [orgVersion, setOrgVersion] = useState(undefined);
  const [orgResults, setOrgResults] = useState(null); // validate-roster の results 配列
  const [orgChecking, setOrgChecking] = useState(false);

  // ─── 会社名検証（系列会社ありグループのみ） ───
  // 選択中グループの会社名ルール（系列会社ありグループのみ GET /api/group/:code/company-list を 1 回呼ぶ。
  // 取得完了まで・失敗時は取得済みのグループ一覧の系列会社マスタを使う）
  const selectedGroup = useMemo(
    () => groups.find((g) => g.group_code === groupCode),
    [groups, groupCode]
  );
  const companyRule = useCompanyRule(groupCode, selectedGroup);
  // 系列会社ありグループか（validate-roster に company を送るか）。グループ選択直後の検証でも使うため
  // 非同期の会社名ルールではなく、取得済みのグループ一覧の has_affiliates で判定する
  const isAffiliateGroup = (gCode) =>
    buildCompanyRule(groups.find((g) => g.group_code === gCode)).enabled;
  // 会社名エラー行（company 列がある行のみ。列が無い行は会社名を更新しないため対象外。権限を問わず空欄・マスタ外はエラー）。
  // 系列会社ありグループでは「所属フルパス」先頭の会社名で補完した行・会社名列との食い違いも対象。
  // 検証に既存ユーザーの情報は不要なため、ファイル読込時点で確定する
  const companyErrors = useMemo(() => {
    if (!companyRule.enabled || !Array.isArray(fileJSON)) return [];
    return collectCompanyErrors(companyRule, fileJSON, { isTarget: hasCompanyKey });
  }, [companyRule, fileJSON]);

  const onDrop = async (files) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onabort = () => console.log('file reading was aborted');
    reader.onerror = () => console.log('file reading failed');
    reader.onload = (e) => {
      //ファイルの読み込みが終わったら発火
      // decodeCsvBuffer で文字コード自動判定 + 先頭 BOM 除去まで一括処理
      const unicodeString = decodeCsvBuffer(e.target.result);
      Papa.parse(unicodeString, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        complete: async (results) => {
          // 必須列の存在検証（BOM は decodeCsvBuffer で除去済み、その他不可視文字を捕捉）
          const headers = (results.meta && results.meta.fields) || [];
          const v = validateCsvHeaders(headers, REQUIRED_COLUMNS);
          if (!v.ok) {
            message.error({
              content: formatHeaderValidationError(v),
              key: 'csvupload',
              duration: 10,
            });
            setFileJSON(undefined);
            setFileData(
              'エラー：CSVヘッダーを確認の上、再アップロードしてください。'
            );
            return;
          }
          setFileData(unicodeString);
          setFileJSON(results.data);
          // 企業が既に選択済みなら、この CSV を組織マスターで検証する
          if (groupCode) {
            const version = await loadOrgTree(groupCode);
            if (version) runRosterValidation(groupCode, results.data);
            else setOrgResults(null);
          } else {
            setOrgResults(null);
          }
          //const courseInfo = courseMerge(results.data);
          // setProjectKeyValue(`${courseInfo.course_id}_${ymdhms()}`);
          // console.log({ courseInfo });
        },
      });
    };
    reader.readAsArrayBuffer(file); //ファイルを読み込む
    const group = await Axios.get('/api/group/');
    setGroups(group.data.groups);
    // console.log(group.data.groups);
  };

  // ─── 組織マスター（有効バージョン）を取得。無ければ null（従来方式） ───
  const loadOrgTree = async (gCode) => {
    try {
      const res = await Axios.get(`/api/org/tree/${gCode}`);
      if (res.data && res.data.success) {
        const version = res.data.version || null;
        setOrgVersion(version);
        return version;
      }
    } catch (error) {
      console.log({ error });
    }
    setOrgVersion(null);
    return null;
  };

  // ─── CSV 各行の所属を組織マスターと突き合わせ（validate-roster） ───
  const runRosterValidation = async (gCode, rows) => {
    setOrgChecking(true);
    setOrgResults(null);
    try {
      // 系列会社ありグループのみ各行に company を含める（それ以外は従来どおり）
      const res = await Axios.post(`/api/org/validate-roster/${gCode}`, {
        rows: buildRosterRows(rows, { affiliate: isAffiliateGroup(gCode) }),
      });
      if (res.data && res.data.success) {
        setOrgResults(res.data.results || []);
      } else {
        throw new Error('組織検証に失敗しました。');
      }
    } catch (error) {
      console.log({ error });
      message.error({
        content: extractAxiosErrorMessage(error, '組織検証に失敗しました。'),
        key: 'orgcheck',
        duration: 5,
      });
      setOrgResults(null);
    } finally {
      setOrgChecking(false);
    }
  };

  // ─── 組織検証エラー行を CSV（BOM UTF-8）でダウンロード ───
  const downloadOrgErrorCsv = () => {
    // 系列会社ありグループでは会社名エラーも合流させた一覧を出力する
    const errors = companyRule.enabled
      ? displayErrorRows
      : (orgResults || []).filter((r) => !r.ok);
    if (errors.length === 0) return;
    const csv = buildRosterErrorCsv(errors, fileJSON);
    triggerBlobDownload(
      csv,
      `組織検証エラー_${groupCode}_${ymd8()}.csv`,
      'text/csv;charset=utf-8;'
    );
  };

  //yyyymmddhhmmss
  const ymdhms = () => {
    var date = new Date();
    return (
      date.getFullYear() +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      ('0' + date.getDate()).slice(-2) +
      ('0' + date.getHours()).slice(-2) +
      ('0' + date.getMinutes()).slice(-2) +
      ('0' + date.getSeconds()).slice(-2)
      // date.getMilliseconds()
    );
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    let userData = []; //最終分類用
    setProcessing(true);
    try {
      message.loading({
        content: 'CSVアップロード中...（件数が多い場合は時間がかかります）',
        key: 'csvupload',
        duration: 0,
      });

      // CSV が正常に読み込まれているかを確認（必須列検証で弾かれた場合は fileJSON が undefined）
      if (!fileJSON || !Array.isArray(fileJSON) || fileJSON.length === 0) {
        message.error({
          content:
            '有効なCSVファイルがアップロードされていません。CSVを再確認してください。',
          key: 'csvupload',
          duration: 3,
        });
        throw new Error('CSVデータがありません。');
      }

      if (!groupCode) {
        message.error({
          content: '企業コードを入力してください。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('企業コードがありません。');
      }
      const [res, res4] = await Promise.all([
        Axios.get(`/api/group/${groupCode}`),
        Axios.get(`/api/users/group/${groupCode}`),
      ]);
      // console.log({ res4 });
      let users;
      // const res = await Axios.get(`/api/group/${groupCode}`);
      if (res.data.success && res4.data.success) {
        users = res4.data.users;
        // console.log(res.data.group);
        if (res.data.group.department) {
          department = res.data.group.department;
        }
        if (res.data.group.position) {
          position = res.data.group.position;
        }
        if (res.data.group.emp_type) {
          empType = res.data.group.emp_type;
        }
        if (res.data.group.level_code) {
          levelCode = res.data.group.level_code;
        }
        if (res.data.group.go_out) {
          goOut = res.data.group.go_out;
        }
      } else {
        message.error({
          content: '企業コードを確認してください。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('企業情報の取得ができませんでした。');
      }
      // 組織マスターがある場合のみ、エラー行集合を作る
      // ※ C′ 設計（2026-07）で org_code の dual-write を廃止したため、
      //   ここでは検証エラー行の除外にのみ validate-roster の結果を使う（org_code は付与しない）
      const hasOrgMaster = !!orgVersion;
      const orgErrorRowSet = new Set();
      if (hasOrgMaster && orgResults) {
        orgResults.forEach((r) => {
          if (!r.ok) orgErrorRowSet.add(r.row);
        });
      }
      // 系列会社ありグループのみ: 会社名エラー行（ファイル読込時点で画面に表示済み）も更新対象から除外する
      if (companyRule.enabled) {
        companyErrors.forEach((r) => orgErrorRowSet.add(r.row));
      }
      let excludedCount = 0;
      // 履歴（UVD）に記録する既定日時。CSV に input_date 列が無い場合はアップロード日時を使う。
      const today = new Date(Date.now());

      await fileJSON.map((rawRecord, idx) => {
        const rowNo = idx + 1; // 1 起点（validate-roster の row と対応）
        // 組織エラー行（系列会社ありグループでは会社名エラー行も）は更新対象から除外（正常行のみ更新）
        if (orgErrorRowSet.has(rowNo)) {
          excludedCount += 1;
          return;
        }
        // 「所属フルパス」列があれば department〜department7 に展開（個別列形式は不変）し、
        // 所属情報がある行は未記載の階層を '' で補完（所属 7 階層を 1 単位として扱い、旧下位階層の残留を防ぐ）
        // 系列会社ありグループでは「所属フルパス」が「会社名／…」形式のため先頭を会社名として分解する
        const record = completeDepartmentChain(
          normalizeRecord(rawRecord, { affiliate: companyRule.enabled })
        );
        // 所属名の入力規則（「／」禁止・制御文字禁止・50 文字以内）に反する行があれば中断
        const deptNameError = validateRecordDepartments(record);
        if (deptNameError) {
          throw new Error(`${rowNo} 行目: ${deptNameError}`);
        }
        // 所属の経路プレフィックス（本社 / 本社／営業部 / …）を企業マスター（GSD）へ登録（7 階層まで）
        buildDepartmentPrefixes(record).forEach((prefix) => {
          if (!department.find((item) => item === prefix)) department.push(prefix);
        });
        let dpString = '';
        if (record.position) {
          dpString = record.position;
          if (!position.find((item) => item === dpString)) {
            position.push(dpString);
          }
        }
        if (record.emp_type) {
          dpString = record.emp_type;
          if (!empType.find((item) => item === dpString)) {
            empType.push(dpString);
          }
        }
        if (record.level_code) {
          dpString = record.level_code;
          if (!levelCode.find((item) => item === dpString)) {
            levelCode.push(dpString);
          }
        }
        if (record.go_out) {
          dpString = record.go_out;
          if (!goOut.find((item) => item === dpString)) {
            goOut.push(dpString);
          }
        }

        let element = {};
        element.email = record.email;
        if (record['lastname'] !== undefined)
          element.lastname = record.lastname;
        if (record['lastname_yomi'] !== undefined)
          element.lastname_yomi = record.lastname_yomi;
        if (record['name'] !== undefined) element.name = record.name;
        if (record['name_yomi'] !== undefined)
          element.name_yomi = record.name_yomi;
        if (record['role'] !== undefined) element.role = record.role;
        if (record['department'] !== undefined)
          element.department = record.department;
        if (record['company'] !== undefined) element.company = record.company;
        if (record['department'] !== undefined)
          element.department = record.department;
        if (record['department2'] !== undefined)
          element.department2 = record.department2;
        if (record['department3'] !== undefined)
          element.department3 = record.department3;
        if (record['department4'] !== undefined)
          element.department4 = record.department4;
        if (record['department5'] !== undefined)
          element.department5 = record.department5;
        if (record['department6'] !== undefined)
          element.department6 = record.department6;
        if (record['department7'] !== undefined)
          element.department7 = record.department7;
        if (record['position'] !== undefined)
          element.position = record.position;
        if (record['manager'] !== undefined) element.manager = record.manager;
        if (record['level_code'] !== undefined)
          element.level_code = record.level_code;
        if (record['go_out'] !== undefined) element.go_out = record.go_out;
        if (record['inactive'] !== undefined) {
          element.inactive = record.inactive;
        } else {
          element.inactive = false;
        }
        if (record['age'] !== undefined) element.age = record.age;
        // 性別は「男／女」でも受け付け 1／2 へ変換（数値・空はそのまま）
        if (record['sex'] !== undefined) {
          element.sex = record.sex === '男' ? 1 : record.sex === '女' ? 2 : record.sex;
        }
        if (record['work_term'] !== undefined)
          element.work_term = record.work_term;
        if (record['change_password'] !== undefined)
          element.change_password = record.change_password;
        if (record['emp_no'] !== undefined) element.emp_no = record.emp_no;
        if (record['emp_type'] !== undefined)
          element.emp_type = record.emp_type;
        if (record['birthday'] !== undefined)
          element.birthday = record.birthday;
        if (record['join_company'] !== undefined)
          element.join_company = record.join_company;
        if (record['job_type'] !== undefined)
          element.job_type = record.job_type;
        if (record['age_range'] !== undefined)
          element.age_range = record.age_range;
        if (record['work_location'] !== undefined)
          element.work_location = record.work_location;
        if (record['work_time_rule'] !== undefined)
          element.work_time_rule = record.work_time_rule;
        if (record['work_time'] !== undefined)
          element.work_time = record.work_time;
        if (record['join_type'] !== undefined)
          element.join_type = record.join_type;
        // ★パスワード関連の優先順位はサーバー（PATCH /api/users/csvuserupdate）で決定する:
        //   password に値がある行 → init_pass=その平文・change_password=false を自動設定し、
        //   CSV の init_pass / change_password 列の値は無視。password が空の行のみ両列をそのまま反映。
        if (record['init_pass'] !== undefined)
          element.init_pass = record.init_pass;
        if (record['password'] !== undefined)
          element.password = record.password;
        if (record['e3_department'] !== undefined) {
          element.e3_department = [];
          element.e3_department.push(record.e3_department);
        }
        // 履歴（UVD）は本画面（GSD/UVD あり）の役割そのものなので常に 1 件追記する。
        //   input_date 列があればその日付、無ければアップロード日時を採用。
        //   ※ 旧実装は input_date 列が無いと履歴を残さず、所属だけが書き換わっていた。
        //     その場合「翌年度のシーズンでも過去の所属スナップショットが採用される」ため、
        //     組織表バージョンで年度別に集計する運用では旧所属のまま集計される不具合になっていた。
        {
          const u = users.find((item) => item.email === record.email);
          element.change_history =
            u && Array.isArray(u.change_history) ? u.change_history : [];
          element.change_history.push({
            input_date: record.input_date ? record.input_date : today,
            department:
              record['department'] !== undefined ? record.department : null,
            department2:
              record['department2'] !== undefined ? record.department2 : null,
            department3:
              record['department3'] !== undefined ? record.department3 : null,
            department4:
              record['department4'] !== undefined ? record.department4 : null,
            department5:
              record['department5'] !== undefined ? record.department5 : null,
            department6:
              record['department6'] !== undefined ? record.department6 : null,
            department7:
              record['department7'] !== undefined ? record.department7 : null,
            position: record['position'] !== undefined ? record.position : null,
            level_code:
              record['level_code'] !== undefined ? record.level_code : null,
            emp_type: record['emp_type'] !== undefined ? record.emp_type : null,
            go_out: record['go_out'] !== undefined ? record.go_out : null,
            inactive:
              record['inactive'] !== undefined ? record.inactive : false,
            // 系列会社ありグループのみ会社名を履歴に記録（CSV に company 列があればその値、
            // 無ければ既存ユーザーの現在の会社名）。それ以外のグループはキー自体を付けない
            ...(companyRule.enabled
              ? {
                  company:
                    record['company'] !== undefined
                      ? record.company
                      : u && u.company !== undefined
                      ? u.company
                      : null,
                }
              : {}),
          });
          // console.log(record);
        }
        // if (record.lastname) element.lastname = record.lastname;
        // if (record.name) element.name = record.name;
        // if (record.department) element.department = record.department;
        // if (record.company) element.company = record.company;
        // if (record.department) element.department = record.department;
        // if (record.department2) element.department2 = record.department2;
        // if (record.department3) element.department3 = record.department3;
        // if (record.department4) element.department4 = record.department4;
        // if (record.department5) element.department5 = record.department5;
        // if (record.position) element.position = record.position;
        // if (record.manager) element.manager = record.manager;
        // if (record.level_code) element.level_code = record.level_code;
        // if (record.go_out) element.go_out = record.go_out;
        // if (record.age) element.age = record.age;
        // if (record.sex) element.sex = record.sex;
        // if (record.work_term) element.work_term = record.work_term;
        // if (record.change_password)
        //   element.change_password = record.change_password;
        // if (record.emp_no) element.emp_no = record.emp_no;
        // if (record.emp_type) element.emp_type = record.emp_type;
        // if (record.birthday) element.birthday = record.birthday;
        // if (record.join_company) element.join_company = record.join_company;
        // if (record.age_range) element.age_range = record.age_range;
        // if (record.work_location) element.work_location = record.work_location;
        // if (record.work_time_rule)
        //   element.work_time_rule = record.work_time_rule;
        // if (record.work_time) element.work_time = record.work_time;
        // if (record.join_type) element.join_type = record.join_type;
        // if (record.init_pass) element.init_pass = record.init_pass;
        userData.push(element);
      });

      // 更新対象が 0 件（全行が組織エラー）の場合は中断
      if (userData.length === 0) {
        message.error({
          content:
            '更新できる正常な行がありません。組織検証エラーを解消してください。',
          key: 'csvupload',
          duration: 6,
        });
        throw new Error('更新対象が 0 件です。');
      }

      const groupData = { department, position, empType, levelCode, goOut };
      const [res2, res3] = await Promise.all([
        Axios.patch(`/api/group/${groupCode}`, groupData),
        Axios.patch('/api/users/csvuserupdate', userData),
      ]);

      // Axios.patch(`/api/group/${groupCode}`, department),

      // console.log({ res3 });
      if (res3.data.success && res2.data.success) {
        const excludedText =
          excludedCount > 0
            ? `\n${excludedLabel}：${excludedCount}名`
            : '';
        setFileData('CSVアップロードが完了しました。' + excludedText);
        message.success({
          content: 'アップロード完了！',
          key: 'csvupload',
          duration: 2,
        });
        setTimeout(() => {
          props.history.push('/');
        }, 1000);
      } else {
        message.error({
          content: 'CSVアップロードに失敗しました。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('CSVアップロードに失敗しました。');
      }
    } catch (error) {
      console.log({ error });
      // バックエンドの具体的なエラー本文を抽出して表示（運用担当が pm2 logs 参照不要）
      // サーバーが会社名の検証エラー（company_errors）を返した場合は先頭 5 件を併記する
      const companyDetail = formatCompanyErrorDetail(error);
      message.error({
        content:
          extractAxiosErrorMessage(error, 'CSVアップロードに失敗しました。') +
          companyDetail,
        key: 'csvupload',
        duration: companyDetail ? 10 : 5,
      });
    } finally {
      setProcessing(false);
    }
  };

  const onChangeGroup = async (value) => {
    if (!value) return;
    setGroupCode(value);
    setSubmitButtonDisable(false);
    // 企業選択時に組織マスターの有無を判定し、CSV があれば検証する
    setOrgResults(null);
    const version = await loadOrgTree(value);
    if (version && fileJSON && fileJSON.length > 0) {
      runRosterValidation(value, fileJSON);
    }
  };

  // ─── 組織検証の派生値（レンダー用） ───
  const hasOrgMaster = !!orgVersion; // 組織表あり
  const orgErrorRows = (orgResults || []).filter((r) => !r.ok);
  const orgOkCount = (orgResults || []).filter((r) => r.ok).length;
  const orgHasErrors = hasOrgMaster && orgResults && orgErrorRows.length > 0;
  const orgErrorTableData = orgErrorRows.map((r) => {
    const rec = (fileJSON && fileJSON[r.row - 1]) || {};
    return {
      key: r.row,
      row: r.row,
      name: `${rec.lastname || ''} ${rec.name || ''}`.trim() || '—',
      belong: joinInputBelong(rec) || '—',
      error: r.error || '—',
      suggestions: r.suggestions || [],
    };
  });
  // ─── 会社名検証の合流（系列会社ありグループのみ。それ以外は組織検証の値をそのまま使う） ───
  // サンプル CSV（系列会社ありグループは会社名付き・「会社名／…」形式の例）
  const downloadSampleCsv = () => {
    triggerBlobDownload(
      buildSampleRosterCsv('update', { affiliate: companyRule.enabled }),
      SAMPLE_CSV_FILE_NAMES.update,
      'text/csv;charset=utf-8'
    );
  };
  const companyEnabled = companyRule.enabled;
  const displayErrorRows = companyEnabled
    ? mergeRowErrors(hasOrgMaster && orgResults ? orgErrorRows : [], companyErrors)
    : orgErrorRows;
  const showValidation = companyEnabled
    ? (hasOrgMaster && !!orgResults) || Array.isArray(fileJSON)
    : !!orgResults;
  // 系列会社ありグループの正常件数。組織表がある場合は「組織検証 OK かつ会社名エラー無し」の行だけを数える
  // （所属が未入力で組織検証の対象外になった行は、従来どおり正常件数に含めない＝登録ボタンの活性条件と揃える）
  const companyErrorRowSet = new Set((companyErrors || []).map((r) => r.row));
  const okCount = companyEnabled
    ? hasOrgMaster && orgResults
      ? orgResults.filter((r) => r.ok && !companyErrorRowSet.has(r.row)).length
      : Math.max(0, (Array.isArray(fileJSON) ? fileJSON.length : 0) - displayErrorRows.length)
    : orgOkCount;
  const hasRowErrors = companyEnabled ? displayErrorRows.length > 0 : orgHasErrors;
  const errorTableData = companyEnabled
    ? displayErrorRows.map((r) => {
        const rec = (fileJSON && fileJSON[r.row - 1]) || {};
        return {
          key: r.row,
          row: r.row,
          name: `${rec.lastname || ''} ${rec.name || ''}`.trim() || '—',
          belong: joinInputBelong(rec) || '—',
          error: r.error || '—',
          suggestions: r.suggestions || [],
        };
      })
    : orgErrorTableData;
  // エラー見出し（会社名エラーを含むときだけ文言を切り替える）
  // 系列会社ありグループでは validate-roster の field:'company' も会社名エラーとして数える
  const errorKinds = countErrorKinds(
    hasOrgMaster && orgResults ? orgErrorRows : [],
    companyErrors
  );
  const companyErrorCount = companyEnabled ? errorKinds.company : 0;
  const orgErrorCountShown = companyEnabled
    ? errorKinds.department
    : hasOrgMaster && orgResults
    ? orgErrorRows.length
    : 0;
  const errorHeading =
    companyErrorCount === 0
      ? '所属が組織表と一致しない行があります。'
      : orgErrorCountShown === 0
      ? '会社名が系列会社マスタと一致しない行があります。'
      : '所属が組織表と一致しない行、または会社名に誤りがある行があります。';
  // 完了表示の除外ラベル
  const excludedLabel = companyEnabled ? '除外（検証エラー）' : '除外（組織エラー）';
  const noValidRows =
    (hasOrgMaster && orgResults && orgOkCount === 0) ||
    (companyEnabled &&
      Array.isArray(fileJSON) &&
      fileJSON.length > 0 &&
      okCount === 0);

  return (
    <Fragment>
      <Spin
        spinning={processing}
        fullscreen
        tip="処理中です。完了までこのままお待ちください…"
      />
      <div style={{ maxWidth: '700px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>メンバー情報更新（履歴あり）</Title>
          <Title level={4}>
            GSD : Group Summery Data, VUD : Variable User Data
          </Title>
        </div>
        <Form onSubmit={onSubmit}>
          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❶</Title>
          {/* サンプル CSV（ヘッダー＝英字フィールド名・2026-10-07） */}
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Button icon={<DownloadOutlined />} onClick={downloadSampleCsv}>
              サンプル CSV ダウンロード
            </Button>
            <Text type="secondary" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
              ヘッダーは英字のフィールド名です。所属は「department_path」1 列（「／」区切り{companyRule.enabled ? '・先頭は系列会社名' : ''}）、または従来の department〜department7 の個別列で指定できます。
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* Drop zone */}
            <Dropzone onDrop={onDrop} multiple={false} maxSize={1000000000}>
              {({ getRootProps, getInputProps }) => (
                <div
                  style={{
                    width: '300px',
                    height: '240px',
                    border: '1px solid lightgray',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <PlusOutlined style={{ fontSize: '3rem' }} />
                </div>
              )}
            </Dropzone>
            <div
              style={{
                width: '300px',
                height: '240px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text mark>
                CSVファイルをDrag &
                Dropするか、+を押してファイルを選択してください。
              </Text>
            </div>
          </div>
          <br />
          <div>
            <Title style={{ color: 'var(--brand-primary)' }}>❷</Title>
            {/* <Text strong>企業コードを入力してください（必須）</Text>
            <Input
              placeholder="一意になる企業コードを入力してください。"
              allowClear
              onChange={onChangeGroupCode}
              value={groupCode}
            /> */}
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-4)' }}>企業を選択してください。（必須）</Text>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Select
                showSearch
                style={{ marginLeft: 0, marginRight: 10, width: 300 }}
                placeholder="企業選択"
                optionFilterProp="children"
                onChange={onChangeGroup}
                value={groupCode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {groups.map((element) => {
                  // console.log({ element });
                  return (
                    <Option value={element.group_code} key={element._id}>
                      {element.group_name}
                    </Option>
                  );
                })}
              </Select>
            </div>
            <br />
          </div>

          {/* ─── 組織マスター検証（名簿連携） ─── */}
          {groupCode && orgVersion === null && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 'var(--space-4)' }}
              message="この企業は組織表が未登録のため、従来方式で更新されます。"
            />
          )}
          {groupCode && (hasOrgMaster || companyEnabled) && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Spin spinning={orgChecking}>
                {showValidation && (
                  <div>
                    <Space wrap style={{ marginBottom: 'var(--space-3)' }}>
                      <StatusBadge tone="success">
                        正常 {okCount} 件
                      </StatusBadge>
                      <StatusBadge tone={hasRowErrors ? 'error' : 'neutral'}>
                        エラー {displayErrorRows.length} 件
                      </StatusBadge>
                    </Space>

                    {hasRowErrors && (
                      <div>
                        <Space
                          wrap
                          style={{ marginBottom: 'var(--space-2)' }}
                        >
                          <Text strong style={{ color: 'var(--red-active)' }}>
                            {errorHeading}
                          </Text>
                          <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={downloadOrgErrorCsv}
                          >
                            エラーリストをCSVでダウンロード
                          </Button>
                        </Space>
                        <Table
                          size="small"
                          rowKey="key"
                          columns={ORG_ERROR_COLUMNS}
                          dataSource={errorTableData}
                          pagination={
                            errorTableData.length > 10
                              ? { pageSize: 10 }
                              : false
                          }
                          scroll={{ y: 280 }}
                        />
                        <Text
                          type="secondary"
                          style={{
                            display: 'block',
                            marginTop: 'var(--space-2)',
                          }}
                        >
                          ※ エラー行を除いた正常行のみ更新されます。
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Spin>
            </div>
          )}

          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❸</Title>
          <Button
            type="primary"
            size="large"
            onClick={onSubmit}
            disabled={submitButtonDisable || processing || noValidRows}
            loading={processing}
          >
            {hasRowErrors ? 'エラー行を除いて更新' : 'アップデート'}
          </Button>
          {noValidRows && (
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: 'var(--space-2)' }}
            >
              {companyEnabled
                ? '更新できる正常な行がありません。検証エラーを解消してください。'
                : '更新できる正常な行がありません。組織検証エラーを解消してください。'}
            </Text>
          )}
        </Form>
        <TextArea rows={20} value={fileData} />
      </div>
    </Fragment>
  );
}

export default CSVUserUpdatePage;
